import { ComponentPropsWithoutRef, FC, ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Edit } from 'jbc-front/components/icons';
import { DescriptionContainer, HoverDescription } from 'jbc-front/components/presenters/ui/HoverDescription';
import { useSession } from '@/components/SessionProvider';
import { hasPermission } from '@/utils/permission';
import { makeBackUrl } from '@/utils/url';
import { DeleteUser } from '../DeleteUser';
import { RoleCode, User } from '../../query';
import { extractRoles } from '../../../utils';
import styles from './UsersTable.scss';

type RoleOption = { label: string; value: RoleCode | '' };

interface RoleCellProps {
  role: RoleCode | null;
  roleOptions: RoleOption[];
}

const RoleCell: FC<RoleCellProps> = ({ role, roleOptions }) => {
  const value = roleOptions.find(({ value }) => value === role) || roleOptions[0];
  return <td className={styles.roleCell}>{value.label}</td>;
};

interface IconButtonProps extends ComponentPropsWithoutRef<'div'> {
  text: ReactNode;
  children: ReactNode;
}

const Description: FC<IconButtonProps> = ({ text, children, ...rest }) => {
  const [isHover, setIsHover] = useState(false);

  return (
    <DescriptionContainer {...rest} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
      {children}
      <HoverDescription isHover={isHover} position="top" margin="32px">
        <span className={styles.bold}>{text}</span>
      </HoverDescription>
    </DescriptionContainer>
  );
};

interface RowProps {
  user: User;
  refetch: () => void;
}

export const Row: FC<RowProps> = ({ user, refetch }) => {
  const { user: currentUser } = useSession();
  const location = useLocation();

  const { stressCheckRole, healthCheckRole, userManageRole } = extractRoles(user.roles, null);

  return (
    <tr className={styles.row}>
      <td>{user.employee ? user.employee.staffCode || '-' : '従業員登録なし'}</td>
      <td>{user.employee ? `${user.employee.lastName} ${user.employee.firstName}` : user.name || '-'}</td>
      <td>{user.email}</td>
      {hasPermission(currentUser, 'user_manage_edit_stress_check_roles') && (
        <RoleCell
          role={stressCheckRole}
          roleOptions={[
            { label: '権限なし', value: '' },
            { label: '実施者', value: 'stress_check_practitioner' },
            { label: '実務担当者', value: 'stress_check_person_in_charge' }
          ]}
        />
      )}
      {hasPermission(currentUser, 'user_manage_edit_regular_health_checkup_roles') && (
        <RoleCell
          role={healthCheckRole}
          roleOptions={[
            { label: '権限なし', value: '' },
            { label: '権限あり', value: 'regular_health_checkup_user' }
          ]}
        />
      )}
      {hasPermission(currentUser, 'user_manage_edit_user_manage_roles') && (
        <RoleCell
          role={userManageRole}
          roleOptions={[
            { label: '権限なし', value: '' },
            { label: '権限あり', value: 'user_manager' }
          ]}
        />
      )}
      <td>
        {user.id === currentUser.id ? (
          <Description
            className={classNames(styles.iconContainer, styles.iconContainerDisabled)}
            text="自分自身の権限を変更することはできません">
            <span className={classNames(styles.icon, styles.iconDisabled)}>
              <Edit size={20} />
            </span>
          </Description>
        ) : (
          <Description className={styles.iconContainer} text="編集">
            <Link className={styles.icon} to={`/settings/users/${user.id}/edit?back_to=${makeBackUrl(location)}`}>
              <Edit size={20} />
            </Link>
          </Description>
        )}
        {hasPermission(currentUser, 'user_manage_delete_user') && user.id !== currentUser.id && (
          <Description className={styles.iconContainer} text="削除">
            <DeleteUser className={styles.icon} user={user} refetch={refetch} />
          </Description>
        )}
      </td>
    </tr>
  );
};
